import { Phone, Mail, MapPin, Clock, ArrowUpRight, Truck } from "lucide-react";
import { SITE } from "../../lib/site-data";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28 bg-[#1F1B16] text-[#F8F4ED] overflow-hidden"
    >
      <div className="absolute inset-0 bg-stripes opacity-20" aria-hidden />
      <div className="absolute -top-20 right-10 w-[420px] h-[420px] rounded-full bg-[#8B2332]/30 blur-3xl" aria-hidden />
      <div className="absolute -bottom-32 -left-10 w-[360px] h-[360px] rounded-full bg-[#D4A24C]/15 blur-3xl" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <div className="eyebrow text-[#D4A24C] mb-3">Get In Touch</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Ready when you are — call, text, or book online.
            </h2>
            <p className="mt-4 text-[#F1E9DA]/80 leading-relaxed max-w-lg">
              Got questions about a trailer, a custom rental period, or
              delivery? Reach out — we answer day and night.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <ContactRow
                icon={<Phone className="w-4 h-4" />}
                label="Phone"
                value={SITE.phone}
                href={SITE.phoneHref}
                testId="contact-phone"
              />
              <ContactRow
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                value={SITE.email}
                href={SITE.emailHref}
                testId="contact-email"
              />
              <ContactRow
                icon={<MapPin className="w-4 h-4" />}
                label="Located in"
                value={SITE.address}
                testId="contact-address"
              />
              <ContactRow
                icon={<Clock className="w-4 h-4" />}
                label="Hours"
                value={SITE.hours}
                testId="contact-hours"
              />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-book-btn"
                className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold"
              >
                Book Online <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="contact-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border-2 border-[#F8F4ED] text-[#F8F4ED] hover:bg-[#F8F4ED] hover:text-[#1F1B16] transition"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
          </div>

          <div
            className="relative rounded-2xl overflow-hidden border border-[#3A332B] shadow-2xl bg-gradient-to-br from-[#3A332B] via-[#1F1B16] to-[#0E0C09] h-[420px] flex flex-col items-center justify-center text-center px-8"
            data-testid="contact-service-area"
          >
            <div className="absolute inset-0 bg-stripes opacity-30" aria-hidden />
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#8B2332]/25 blur-3xl" aria-hidden />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#D4A24C]/15 blur-3xl" aria-hidden />

            <div className="relative w-16 h-16 rounded-2xl bg-[#8B2332] text-white flex items-center justify-center shadow-lg mb-5">
              <Truck className="w-7 h-7" />
            </div>
            <div className="relative eyebrow text-[#D4A24C] mb-3">
              Service Area
            </div>
            <h3 className="relative font-display text-2xl sm:text-3xl font-bold text-[#F8F4ED] leading-tight max-w-sm">
              {SITE.serviceArea}
            </h3>
            <p className="relative mt-4 text-sm text-[#F1E9DA]/75 max-w-xs leading-relaxed">
              Pick-up location shared at booking. Delivery available — call for
              a quote.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ icon, label, value, href, testId }) => {
  const inner = (
    <div className="flex items-start gap-3 bg-white/5 hover:bg-white/10 transition border border-white/10 rounded-xl p-4">
      <div className="w-9 h-9 rounded-lg bg-[#8B2332] text-white flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A24C] font-semibold">
          {label}
        </div>
        <div className="font-display font-semibold mt-0.5 break-words">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block">
      {inner}
    </a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
};
